import PropTypes from "prop-types";
import React from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import { Alert, Link, SkeletonLoader, Table, Title } from "@yoast/ui-library";
import { PageHeader } from "../PageHeader";
import useProvisioningDownloads from "../../hooks/useProvisioningDownloads";
import { productLabel } from "../../functions/provisionedSubscription";
import * as styles from "./ProvisioningStyles.module.scss";

const messages = defineMessages( {
	title: {
		id: "provisioning.downloads.title",
		defaultMessage: "Downloads",
	},
	intro: {
		id: "provisioning.downloads.intro",
		defaultMessage: "The latest plugin versions for each of your product codes.",
	},
	headerPlugin: {
		id: "provisioning.downloads.header.plugin",
		defaultMessage: "Plugin",
	},
	headerSlug: {
		id: "provisioning.downloads.header.slug",
		defaultMessage: "Slug",
	},
	headerVersion: {
		id: "provisioning.downloads.header.version",
		defaultMessage: "Version",
	},
	headerDownload: {
		id: "provisioning.downloads.header.download",
		defaultMessage: "Download",
		description: "Visually hidden header label for the download column.",
	},
	download: {
		id: "provisioning.downloads.download",
		defaultMessage: "Zip",
	},
	empty: {
		id: "provisioning.downloads.empty",
		defaultMessage: "No products are linked to your account.",
	},
	noVersions: {
		id: "provisioning.downloads.noVersions",
		defaultMessage: "No downloads available for this product.",
	},
	loadError: {
		id: "provisioning.downloads.loadError",
		defaultMessage: "We could not load your downloads. Please refresh the page.",
	},
	productLoadError: {
		id: "provisioning.downloads.productLoadError",
		defaultMessage: "We could not load the downloads for this product.",
	},
} );

/**
 * The versions of one product code.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The table or its replacement state.
 */
function ProductVersionsTable( { versions, isLoading = false, isError = false } ) {
	if ( isError ) {
		return (
			<Alert variant="error" role="alert">
				<FormattedMessage { ...messages.productLoadError } />
			</Alert>
		);
	}
	if ( ! isLoading && versions.length === 0 ) {
		return (
			<p className={ styles.empty }>
				<FormattedMessage { ...messages.noVersions } />
			</p>
		);
	}

	return (
		<div className={ styles.tableContainer }>
			<Table>
				<Table.Head>
					<Table.Row>
						<Table.Header><FormattedMessage { ...messages.headerPlugin } /></Table.Header>
						<Table.Header><FormattedMessage { ...messages.headerSlug } /></Table.Header>
						<Table.Header><FormattedMessage { ...messages.headerVersion } /></Table.Header>
						<Table.Header>
							<span className="yst-sr-only">
								<FormattedMessage { ...messages.headerDownload } />
							</span>
						</Table.Header>
					</Table.Row>
				</Table.Head>
				<Table.Body>
					{ isLoading
						? (
							<Table.Row>
								<Table.Cell><SkeletonLoader>Yoast SEO Premium</SkeletonLoader></Table.Cell>
								<Table.Cell><SkeletonLoader>wordpress-seo-premium</SkeletonLoader></Table.Cell>
								<Table.Cell><SkeletonLoader>00.0</SkeletonLoader></Table.Cell>
								<Table.Cell><SkeletonLoader>Zip</SkeletonLoader></Table.Cell>
							</Table.Row>
						)
						: versions.map( version => (
							<Table.Row key={ version.slug }>
								<Table.Cell>{ version.name }</Table.Cell>
								<Table.Cell><code>{ version.slug }</code></Table.Cell>
								<Table.Cell>{ version.version }</Table.Cell>
								<Table.Cell>
									<Link href={ version.downloadUrl }>
										<FormattedMessage { ...messages.download } />
									</Link>
								</Table.Cell>
							</Table.Row>
						) ) }
				</Table.Body>
			</Table>
		</div>
	);
}

ProductVersionsTable.propTypes = {
	versions: PropTypes.arrayOf( PropTypes.shape( {
		name: PropTypes.string,
		slug: PropTypes.string.isRequired,
		version: PropTypes.string,
		downloadUrl: PropTypes.string,
	} ) ).isRequired,
	isLoading: PropTypes.bool,
	isError: PropTypes.bool,
};

/**
 * The latest plugin versions per product code, one table per product.
 *
 * @returns {JSX.Element} The page.
 */
function ProvisioningDownloadsPage() {
	const { downloads, isLoading, isError } = useProvisioningDownloads();

	let body;
	if ( isError ) {
		body = (
			<Alert variant="error" role="alert">
				<FormattedMessage { ...messages.loadError } />
			</Alert>
		);
	} else if ( isLoading && downloads.length === 0 ) {
		body = <SkeletonLoader>Loading products…</SkeletonLoader>;
	} else if ( downloads.length === 0 ) {
		body = (
			<p className={ styles.empty }>
				<FormattedMessage { ...messages.empty } />
			</p>
		);
	} else {
		body = downloads.map( product => (
			<div key={ product.productCode } className={ styles.productGroup }>
				<Title as="h2" size="3">
					{ productLabel( { name: product.name, code: product.productCode } ) }
				</Title>
				<ProductVersionsTable
					versions={ product.versions || [] }
					isLoading={ product.isLoading }
					isError={ product.isError }
				/>
			</div>
		) );
	}

	return (
		<div>
			<PageHeader title={ messages.title } message={ messages.intro } />
			<section className={ styles.section }>
				{ body }
			</section>
		</div>
	);
}

export default ProvisioningDownloadsPage;
